<template>
  <SetupBulkUpload
    :value="open"
    :test-ids="shellTestIds"
    :heading="drawerHeading"
    :step1-description="step1Description"
    :step2-title="step2Title"
    :step2-description="step2Description"
    :has-preview="hasPreview"
    :validating="parsing"
    :file-name="selectedFileName"
    :reupload-visible="hasPreview || parsing"
    :file="files"
    :reset-dropzone="resetTrigger"
    file-accept=".xlsx,.xls,.xlsm,.xlsb,.csv"
    :single-file="true"
    dropzone-accept=".xlsx,.xls,.xlsm,.xlsb,.csv"
    :hide-dropzone-details="false"
    :footer-visible="hasErrors || hasFileDataError || showDataWithError"
    :error-footer-visible="hasErrors"
    :error-footer-text="errorFooterText"
    :error-filter-visible="hasFileDataError || showDataWithError"
    :show-errors-only="showDataWithError"
    :busy="loading"
    :can-submit="canSubmit"
    :submit-label="submitLabel"
    @cancel="onCancel"
    @download="onDownloadTemplate"
    @files-change="onFilesUpdate"
    @reupload="reuploadDoc"
    @upload="onSubmit"
    @update:showErrorsOnly="showDataWithError = $event"
  >
    <template #preview>
      <lb-datatableaction
        ref="previewTable"
        :headers="tableHeaders"
        :tabledata="filteredPreviewRows"
        :displayed-row-count="filteredPreviewRows.length"
        :itemsperpage="filteredPreviewRows.length || 25"
        :bulk-actions="false"
        :show-toolbar="false"
        :auto-height="false"
        :table-height="previewTableHeight"
        :page_details="{}"
        :selectfilter="{}"
        header-bg-class="fbc-table-header-bg"
        data-testid="setup-tasklist__bulk-import-datatable"
      >
        <template #tbody>
          <tr
            v-for="entry in filteredPreviewEntries"
            :key="`preview-${entry.originalIndex}`"
            class="bulk-import-dialog__row"
            :data-testid="`setup-tasklist__bulk-import-row--${entry.originalIndex}`"
          >
            <td
              v-for="header in tableHeaders"
              v-show="header.visible !== false"
              :key="`${entry.originalIndex}-${header.value}`"
              class="px-4 single-line"
              :class="cellClass(entry.originalIndex, header)"
              :data-testid="`setup-tasklist__bulk-import-cell--${header.value}`"
            >
              <div
                v-if="cellError(entry.originalIndex, header.value)"
                class="bulk-import-dialog__cell-inner bulk-import-dialog__cell-inner--error error--text"
              >
                <span class="bulk-import-dialog__cell--truncate">
                  {{ cellError(entry.originalIndex, header.value) }}
                </span>
              </div>

              <div
                v-else-if="isOverflowColumn(header.value)"
                class="bulk-import-dialog__overflow-cell"
                data-row-nav-ignore
              >
                <OverflowList
                  :items="overflowParts(entry.row, header.value)"
                  :data-testid="`setup-tasklist__bulk-import-overflow--${header.value}`"
                />
              </div>

              <span
                v-else-if="header.value === 'description'"
                class="bulk-import-dialog__cell--truncate"
              >
                {{ entry.row.description }}
              </span>

              <span v-else>{{ cellDisplay(entry.row, header) }}</span>
            </td>
          </tr>
        </template>
      </lb-datatableaction>
    </template>
  </SetupBulkUpload>
</template>

<script>
import { OverflowList, SetupBulkUpload } from '@/modules/financialclose/components';
import { BULK_IMPORT_MODES } from '../../utils/taskListConstants';
import {
  BULK_IMPORT_ERROR_FOOTER,
  buildBulkPreviewHeaders,
  parseBulkImportExcel,
  downloadBulkEditTemplate,
  hasBulkImportErrors,
  validateBulkImportRows,
  toBulkApiPayloadRows,
  applyServerRowErrors,
  applyValidationRowMeta,
  mergeBulkImportRowErrors,
} from '../../utils/bulkImportParse';
import { getTaskTypeConstants } from '../../utils/taskListHelpers';
import { splitLocationNames } from '@/modules/financialclose/utils/locationName';
import { mapActions } from 'vuex';

const OVERFLOW_COLUMNS = {
  location: {
    primary: 'locationPrimary',
    count: 'locationOverflowCount',
    items: 'locationOverflowItems',
  },
  preparer: {
    primary: 'preparerPrimary',
    count: 'preparerOverflowCount',
    items: 'preparerOverflowItems',
  },
  approver: {
    primary: 'approverPrimary',
    count: 'approverOverflowCount',
    items: 'approverOverflowItems',
  },
  sign_off_approver: {
    primary: 'sign_off_approverPrimary',
    count: 'sign_off_approverOverflowCount',
    items: 'sign_off_approverOverflowItems',
  },
  tags: {
    primary: 'tagsPrimary',
    count: 'tagsOverflowCount',
    items: 'tagsOverflowItems',
  },
  assertions: {
    primary: 'assertionsPrimary',
    count: 'assertionsOverflowCount',
    items: 'assertionsOverflowItems',
  },
  glcode: {
    primary: 'glcodePrimary',
    count: 'glcodeOverflowCount',
    items: 'glcodeOverflowItems',
  },
};

function splitOverflowValue(value, field) {
  if (Array.isArray(value)) {
    return value.flatMap((item) => splitOverflowValue(item, field));
  }
  if (value == null || value === '' || value === '—') return [];
  if (field === 'location') {
    return splitLocationNames(value).filter((name) => name && name !== '—');
  }
  return String(value)
    .split(',')
    .map((part) => part.replace(/\s+/g, ' ').trim())
    .filter((part) => part && part !== '—');
}

const SHELL_TEST_IDS = {
  root: 'setup-tasklist__bulk-import-root',
  drawer: 'setup-tasklist__bulk-import-dialog',
  reuploadInput: 'setup-tasklist__bulk-import-reupload-input',
  body: 'setup-tasklist__bulk-import-body',
  step1: 'setup-tasklist__bulk-import-step-1',
  download: 'setup-tasklist__bulk-import-download-template',
  step2: 'setup-tasklist__bulk-import-step-2',
  reuploadButton: 'setup-tasklist__bulk-import-reupload',
  validating: 'setup-tasklist__bulk-import-validating',
  dropzone: 'setup-tasklist__bulk-import-dropzone',
  dragDrop: 'setup-tasklist__bulk-import-drag-drop',
  preview: 'setup-tasklist__bulk-import-preview',
  errorFilterRow: 'setup-tasklist__bulk-import-error-filter-section',
  errorFilter: 'setup-tasklist__bulk-import-show-errors-checkbox',
  errorFooter: 'setup-tasklist__bulk-import-error-footer',
  errorFooterMessage: 'setup-tasklist__bulk-import-error-footer',
  cancel: 'setup-tasklist__bulk-import-cancel',
  upload: 'setup-tasklist__bulk-import-submit',
};

export default {
  name: 'BulkImportDialog',

  components: {
    OverflowList,
    SetupBulkUpload,
  },

  props: {
    open: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: BULK_IMPORT_MODES.IMPORT,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    cycleType: {
      type: String,
      default: 'FBC',
    },
    templates: {
      type: Array,
      default: () => [],
    },
    /** Selected templates only — used for Bulk Edit download. */
    selectedTemplates: {
      type: Array,
      default: () => [],
    },
    users: {
      type: Array,
      default: () => [],
    },
    locations: {
      type: Array,
      default: () => [],
    },
    chartOfAccounts: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    subcategories: {
      type: Array,
      default: () => [],
    },
    customColumns: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      shellTestIds: SHELL_TEST_IDS,
      files: [],
      resetTrigger: false,
      parsing: false,
      previewRows: [],
      payloadRows: [],
      rowErrors: [],
      showDataWithError: false,
      errorFooterText: BULK_IMPORT_ERROR_FOOTER,
    };
  },

  computed: {
    isBulkEdit() {
      return this.mode === BULK_IMPORT_MODES.BULK_EDIT;
    },

    hasPreview() {
      return this.previewRows.length > 0;
    },

    hasErrors() {
      return hasBulkImportErrors(this.rowErrors);
    },

    hasFileDataError() {
      return this.previewRows.some((row, index) => this.rowHasError(index, row));
    },

    filteredPreviewEntries() {
      const entries = this.previewRows.map((row, originalIndex) => ({
        row,
        originalIndex,
      }));
      if (!this.showDataWithError || !this.hasFileDataError) return entries;
      return entries.filter(({ row, originalIndex }) =>
        this.rowHasError(originalIndex, row)
      );
    },

    filteredPreviewRows() {
      return this.filteredPreviewEntries.map((entry) => entry.row);
    },

    /** Headers = Excel template columns for this mode + custom fields (no extras). */
    tableHeaders() {
      return buildBulkPreviewHeaders(this.mode, this.customColumns, this.locationHeader);
    },

    locationHeader() {
      return this.$store.state.LocationHeader || 'Location';
    },

    drawerHeading() {
      return this.isBulkEdit ? 'Bulk Edit Task' : 'Bulk Import';
    },

    submitLabel() {
      return this.isBulkEdit ? 'Upload & Update' : 'Upload';
    },

    step1Description() {
      return this.isBulkEdit
        ? 'Start by downloading the standard Excel template containing your current task data.'
        : 'Start by downloading the standard Excel template for importing task templates.';
    },

    step2Title() {
      return this.isBulkEdit ? 'Step 2: Upload Updated File' : 'Step 2: Upload File';
    },

    step2Description() {
      return this.isBulkEdit
        ? 'Save your changes in the Excel file, then drag and drop it here to update your tasks in the system'
        : 'Fill in the Excel file, then drag and drop it here to import your tasks into the system';
    },

    file() {
      if (!this.files || !this.files.length) return null;
      return this.files[0] || null;
    },

    selectedFileName() {
      return this.file?.name || '';
    },

    canSubmit() {
      return (
        this.hasPreview &&
        this.payloadRows.length > 0 &&
        !this.parsing &&
        !this.hasErrors
      );
    },

    /**
     * Content height for few rows; CSS max-height on the preview wrapper
     * scrolls when there are many (same pattern as bulk-edit BulkUpload).
     */
    previewTableHeight() {
      return 'auto';
    },

    templateDownloadUrl() {
      const cycle = (this.cycleType || '').trim();
      const base = '/v2/financialclose/masters/checklist/getbulksubmittemplate';
      return cycle ? `${base}/${cycle}` : base;
    },

    /** Bulk Edit download exports only the rows the user selected. */
    templatesForExport() {
      if (this.isBulkEdit) {
        return (this.selectedTemplates || []).length
          ? this.selectedTemplates
          : [];
      }
      return this.templates || [];
    },

    taskTypes() {
      return getTaskTypeConstants(this);
    },
  },

  watch: {
    open(val) {
      if (!val) this.reset();
    },
  },

  methods: {
    ...mapActions('setup', [
      'validateBulkTaskTemplates',
      'updateBulkTaskTemplates',
      'importBulkTaskTemplates',
    ]),

    isOverflowColumn(value) {
      return !!OVERFLOW_COLUMNS[value];
    },

    overflowParts(row, value) {
      const map = OVERFLOW_COLUMNS[value];
      const metaItems = map && Array.isArray(row[map.items])
        ? row[map.items].filter(Boolean)
        : [];
      if (metaItems.length) {
        const primary = map && row[map.primary] && row[map.primary] !== '—'
          ? row[map.primary]
          : '';
        return primary ? [primary, ...metaItems] : metaItems;
      }
      const fromPrimary = map && row[map.primary]
        ? splitOverflowValue(row[map.primary], value)
        : [];
      if (fromPrimary.length > 1) return fromPrimary;
      const fromValue = splitOverflowValue(row[value], value);
      return fromValue.length ? fromValue : fromPrimary;
    },

    cellDisplay(row, header) {
      const val = row[header.value];
      return val == null || val === '' ? '—' : val;
    },

    cellError(rowIndex, field) {
      const fromRow = (this.previewRows[rowIndex] || {})._errors || {};
      if (fromRow[field]) return fromRow[field];
      const errors = this.rowErrors[rowIndex] || {};
      return errors[field] || '';
    },

    rowHasError(rowIndex, row = this.previewRows[rowIndex]) {
      const inlineErrors = row?._errors || {};
      if (Object.values(inlineErrors).some(Boolean)) return true;
      const errors = this.rowErrors[rowIndex] || {};
      return Object.values(errors).some(Boolean);
    },

    cellClass(rowIndex, header) {
      const classes = [];
      if (header.value === 'description') {
        classes.push('bulk-import-dialog__cell--truncate');
      }
      if (header.value === 'ref') {
        classes.push('bulk-import-dialog__cell--ref');
      }
      if (this.cellError(rowIndex, header.value)) {
        classes.push('bulk-import-dialog__cell--error');
      }
      return classes;
    },

    async onFilesUpdate(files) {
      const list = Array.isArray(files) ? files : files ? [files] : [];
      this.files = list.length ? [list[list.length - 1]] : [];
      if (!this.files.length) {
        this.previewRows = [];
        this.payloadRows = [];
        this.rowErrors = [];
        this.showDataWithError = false;
        return;
      }
      await this.parseSelectedFile();
    },

    async parseSelectedFile() {
      const file = this.file;
      if (!file) return;

      this.parsing = true;
      this.previewRows = [];
      this.payloadRows = [];
      this.rowErrors = [];
      this.showDataWithError = false;

      try {
        const ext = (file.name || '').split('.').pop().toLowerCase();
        if (!['xlsx', 'xls', 'xlsm', 'xlsb', 'csv'].includes(ext)) {
          throw new Error('Only xlsx, xls, xlsm, xlsb, csv file extensions are supported');
        }

        let sheetRows;
        const preferredSheet = 'TOP SHEET';
        try {
          sheetRows = await this.$nova.readExcelFile(file, preferredSheet);
        } catch (sheetErr) {
          sheetRows = await this.$nova.readExcelFile(file, '');
        }

        const { payload, preview } = parseBulkImportExcel(sheetRows, {
          mode: this.mode,
          taskTypes: this.taskTypes,
          templates: this.templates,
          customColumns: this.customColumns,
          cycleType: this.cycleType,
          locationHeader: this.locationHeader,
        });

        // API body: 1 Excel row = 1 data[] item (same order)
        this.payloadRows = toBulkApiPayloadRows(payload, {
          mode: this.mode,
          cycleType: this.cycleType,
        });
        this.previewRows = preview.map((row) => ({ ...row }));
        this.rowErrors = preview.map(() => ({}));

        await this.runServerValidation(this.previewRows);
        this.applyClientPreviewValidation();
      } catch (err) {
        this.files = [];
        this.resetDragDrop();
        this.$store.commit('sbError', err?.message || err || 'Failed to read Excel file');
      } finally {
        this.parsing = false;
      }
    },

    async runServerValidation(previewDraft = null) {
      if (!this.payloadRows?.length) return;

      const basePreview = Array.isArray(previewDraft)
        ? previewDraft.map((row) => ({ ...row }))
        : this.previewRows.map((row) => ({ ...row }));

      try {
        const result = await this.validateBulkTaskTemplates({
          data: this.payloadRows,
          mode: this.isBulkEdit ? 'update' : 'create',
        });

        // API returns { status, message, data: { valid, rowErrors, rowMeta, groups } }
        const data =
          result?.data?.valid != null || Array.isArray(result?.data?.rowErrors)
            ? result.data
            : result?.data?.data || {};
        const serverErrors = Array.isArray(data.rowErrors) ? data.rowErrors : [];
        const validationRowMeta = Array.isArray(data.rowMeta) ? data.rowMeta : [];
        const valid = data.valid === true && serverErrors.length === 0;

        if (!valid && serverErrors.length) {
          const mapped = applyServerRowErrors(basePreview, serverErrors);
          this.previewRows = mapped.preview;
          this.rowErrors = mapped.rowErrors;
        } else if (!valid) {
          // valid=false but no rowErrors — keep rows, block submit via synthetic row error
          this.rowErrors = basePreview.map(() => ({}));
          if (basePreview.length) {
            const msg = result?.message || 'Validation failed';
            this.previewRows = basePreview.map((row, idx) =>
              idx === 0
                ? { ...row, _errors: { description: msg } }
                : { ...row, _errors: {} }
            );
            this.rowErrors = this.previewRows.map((row) => row._errors || {});
          } else {
            this.previewRows = [];
          }
        } else {
          this.previewRows = basePreview.map((row) => ({ ...row, _errors: {} }));
          this.rowErrors = basePreview.map(() => ({}));
        }

        this.applyImportValidationRowMeta(validationRowMeta);
      } catch (err) {
        console.error('[BulkImportDialog]: validate error', err);
        const nestedErrors =
          err?.response?.data?.data?.rowErrors ||
          err?.response?.data?.rowErrors ||
          err?.rowErrors ||
          [];
        const nestedRowMeta =
          err?.response?.data?.data?.rowMeta ||
          err?.response?.data?.rowMeta ||
          [];
        if (nestedErrors.length) {
          const mapped = applyServerRowErrors(basePreview, nestedErrors);
          this.previewRows = mapped.preview;
          this.rowErrors = mapped.rowErrors;
          this.applyImportValidationRowMeta(nestedRowMeta);
          return;
        }
        this.$store.commit(
          'sbError',
          err?.response?.data?.message || err?.message || 'Validation request failed'
        );
      }
    },

    /**
     * Client preview checks (import + bulk edit): required fields, duplicates,
     * empty Location, GL shape. Merged with server rowErrors; blocks Upload.
     * Inactive/unknown user and unknown location name come from bulkvalidate.
     */
    applyClientPreviewValidation() {
      if (!this.payloadRows?.length) return;
      const clientErrors = validateBulkImportRows(this.payloadRows, {
        mode: this.mode,
        chartOfAccounts: this.chartOfAccounts,
        categories: this.categories,
        subcategories: this.subcategories,
        taskTypes: this.taskTypes,
        users: this.users,
        locations: this.locations,
        locationHeader: this.locationHeader,
      });
      this.rowErrors = mergeBulkImportRowErrors(this.rowErrors, clientErrors);
      this.previewRows = (this.previewRows || []).map((row, idx) => ({
        ...row,
        _errors: this.rowErrors[idx] || {},
      }));
      console.log('[BulkImportDialog]:', {
        rowCount: this.previewRows.length,
        hasErrors: hasBulkImportErrors(this.rowErrors),
      });
    },

    /** Bulk Import only — show Reference from validation rowMeta (preview-only). */
    applyImportValidationRowMeta(rowMeta) {
      rowMeta = rowMeta || [];
      if (this.isBulkEdit) return;
      this.previewRows = applyValidationRowMeta(this.previewRows, rowMeta);
    },

    /** Apply rowErrors returned from commit failure onto the preview table. */
    applyCommitRowErrors(serverRowErrors = []) {
      const mapped = applyServerRowErrors(this.previewRows, serverRowErrors);
      this.previewRows = mapped.preview;
      this.rowErrors = mapped.rowErrors;
      this.applyClientPreviewValidation();
    },

    async onDownloadTemplate() {
      try {
        if (this.isBulkEdit) {
          if (!this.templatesForExport?.length) {
            this.$store.commit(
              'sbError',
              'Select at least one task to download the bulk edit template'
            );
            return;
          }
          await downloadBulkEditTemplate({
            templates: this.templatesForExport,
            users: this.users,
            locations: this.locations,
            customColumns: this.customColumns,
            cycleType: this.cycleType,
            taskTypes: this.taskTypes,
            locationHeader: this.locationHeader,
            title: 'Tasklist List',
            filename: 'Tasklist.xlsx',
          });
        } else {
          this.$nova.downloadFile('', {}, this.templateDownloadUrl);
        }
        this.$emit('download-template', { mode: this.mode, cycleType: this.cycleType });
      } catch (err) {
        this.$store.commit('sbError', err?.message || err || 'Failed to download template');
      }
    },

    onCancel() {
      this.reset();
      this.$emit('cancel');
    },

    onSubmit() {
      if (!this.canSubmit) return;
      this.commitBulkPayload();
    },

    /**
     * Bulk Edit → POST /masters/checklist/bulkUpdateMasterTaskList
     * Bulk Import → POST /masters/checklist/bulkadd
     */
    async commitBulkPayload() {
      if (!this.payloadRows?.length) {
        this.$store.commit('sbError', 'No rows to upload');
        return;
      }

      this.$emit('update:loading', true);
      this.$emit('submitting', true);

      try {
        if (this.isBulkEdit) {
          await this.updateBulkTaskTemplates({ data: this.payloadRows });
        } else {
          await this.importBulkTaskTemplates({ data: this.payloadRows });
        }
        this.$emit('success', {
          mode: this.mode,
          data: this.payloadRows,
        });
        this.reset();
      } catch (err) {
        console.error('[BulkImportDialog]: commit error', err);
        const rowErrors =
          err?.rowErrors ||
          err?.response?.data?.data?.rowErrors ||
          [];
        if (rowErrors.length) {
          this.applyCommitRowErrors(rowErrors);
        }
        this.$emit('error', err);
      } finally {
        this.$emit('update:loading', false);
        this.$emit('submitting', false);
      }
    },

    resetUploadState() {
      this.files = [];
      this.previewRows = [];
      this.payloadRows = [];
      this.rowErrors = [];
      this.showDataWithError = false;
      this.parsing = false;
      this.resetDragDrop();
    },

    resetDragDrop() {
      this.resetTrigger = false;
      this.$nextTick(() => {
        this.resetTrigger = true;
      });
    },

    reuploadDoc() {
      this.resetUploadState();
    },

    reset() {
      this.resetUploadState();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './BulkImportDialog.scss';
</style>
